import {
    forwardRef,
    useImperativeHandle,
    useState,
} from 'react'

const SECTIONS = [
    { id: 'fcra', label: 'FCRA Disclosure' },
    { id: 'universal_rights', label: 'Universal Right to a Copy & Dispute' },
    { id: 'summary', label: 'Summary of Rights' },
    { id: 'dppa_glba', label: 'DPPA & GLBA' },
    { id: 'esign', label: 'ESIGN Act' },
]

const FcraDisclosureContent = ({ company }) => (
    <div className="space-y-4 text-gray-700 text-sm">
        <p className="font-semibold text-gray-800 italic">PLEASE READ CAREFULLY:</p>
        <p>
            <span className="font-semibold">{company?.name ?? '[Employer/Client Name]'} (the "Company")</span> may
            obtain an identity verification report (which is a form of "consumer report") about you from{' '}
            <span className="font-semibold">VerifiedID LLC and its authorized agents</span> (acting as a
            third-party consumer reporting agency) for employment purposes, specifically as a prerequisite
            for interview scheduling and shortlisting.
        </p>
        <p>
            As part of this "TruCheck" process, you will be the subject of a report which includes
            information about your identity, personal characteristics, and professional credentials.
            These reports may contain:
        </p>
        <ul className="list-disc list-inside space-y-1">
            <li>
                <strong>Identity Authentication:</strong> Validation of government-issued identification.
            </li>
            <li>
                <strong>Employment Eligibility Verification:</strong> Review of citizenship, residency, or
                visa status (e.g., I-94, I-797, EAD) to determine{' '}
                <strong>eligibility for placement or employment</strong>.
            </li>
            <li>
                <strong>Biometric Comparison:</strong> A facial "Match Selfie" compared against your provided
                identification.
            </li>
            <li>
                <strong>Identifier Verification:</strong> Confirmation of identifiers such as email, and the last 4 digits of your SSN.
            </li>
        </ul>
        <p>
            This check is conducted to ensure the integrity of the hiring process and to protect against
            identity fraud.
        </p>
        <div className="bg-gray-100 rounded-lg p-3 text-xs space-y-1 border border-gray-200">
            <p className="font-semibold text-gray-800">VerifiedID LLC is located at:</p>
            <p>11180 State Bridge Road, suite 303, Alpharetta, GA, 30022, USA</p>
        </div>
    </div>
)

const SummaryOfRightsContent = () => (
    <div className="space-y-4 text-gray-700 text-sm">
        <p>
            The federal Fair Credit Reporting Act (FCRA) promotes the accuracy, fairness, and privacy
            of information in the files of consumer reporting agencies.
        </p>
        <ul className="list-disc list-inside space-y-2">
            <li>
                <strong>You must be told if information in your file has been used against you.</strong>{' '}
                Anyone who uses a consumer report to deny your application for employment — including
                the decision to not proceed with an interview — must tell you and provide the contact
                information of the agency <strong>(VerifiedID LLC and its authorized agents)</strong> that
                provided the report.
            </li>
            <li>
                <strong>You have the right to know what is in your file.</strong> You may request and
                obtain all the information about you in the files of a consumer reporting agency.
            </li>
            <li>
                <strong>You have the right to dispute incomplete or inaccurate information.</strong> If
                you identify information in your file that is inaccurate, the agency must investigate.
            </li>
            <li>
                <strong>You must give your consent for reports to be provided to employers.</strong>
            </li>
        </ul>
        <p>
            For more information, visit{' '}
            <a
                href="https://files.consumerfinance.gov/f/201504_cfpb_summary_your-rights-under-fcra.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
            >
                https://files.consumerfinance.gov/f/201504_cfpb_summary_your-rights-under-fcra.pdf
            </a>.
        </p>
    </div>
)

const DppaGlbaContent = () => (
    <div className="space-y-4 text-gray-700 text-sm">
        <div className="space-y-1">
            <h3 className="font-semibold text-gray-800">1. Driver's Privacy Protection Act (DPPA)</h3>
            <p>
                I authorize the collection and verification of information contained in my
                government-issued driver's license or motor vehicle record pursuant to the{' '}
                <strong>Driver's Privacy Protection Act (18 U.S.C. § 2721)</strong> and similar state
                laws. This data is used solely to authenticate my identity and prevent impersonation fraud.
            </p>
        </div>
        <div className="space-y-1">
            <h3 className="font-semibold text-gray-800">2. Gramm-Leach-Bliley Act (GLBA)</h3>
            <p>
                I authorize the collection and use of the <strong>last four digits of my Social
                Security Number</strong> and other non-public personal information (NPI) governed by
                the <strong>Gramm-Leach-Bliley Act (15 U.S.C. § 6801)</strong>. This information is
                used for institutional risk control and to protect against potential identity fraud.
            </p>
        </div>
    </div>
)


const LegalDisclosuresAndConsentHub = forwardRef(({ data, company, refToPrint, handleNext }, ref) => {

    const [expandedSections, setExpandedSections] = useState({ fcra: true, universal_rights: true, summary: true, dppa_glba: true, esign: true })
    const [agreedFcra, setAgreedFcra] = useState(data?.agreedFcra ?? false)
    const [agreedDppaGlba, setAgreedDppaGlba] = useState(data?.agreedDppaGlba ?? false)
    const [agreedEsign, setAgreedEsign] = useState(data?.agreedEsign ?? false)
    const [requestEmailCopy, setRequestEmailCopy] = useState(data?.requestEmailCopy ?? false)
    const [requestEmailCopyDispute, setRequestEmailCopyDispute] = useState(data?.requestEmailCopyDispute ?? false)
    const [error, setError] = useState('')

    const allChecked = agreedFcra && agreedDppaGlba && agreedEsign

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {
            if (!agreedFcra || !agreedDppaGlba || !agreedEsign) {
                setError('Please acknowledge all disclosures to proceed')
                return {
                    isValid: false,
                    value: null,
                }
            }
            setError('')
            return {
                isValid: true,
                value: {
                    agreedFcra,
                    agreedDppaGlba,
                    agreedEsign,
                    requestEmailCopy,
                    requestEmailCopyDispute,
                },
            }
        },
    }))

    const toggleSection = (id) => {
        setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }))
    }

    const getSectionContent = (id) => {
        switch (id) {
            case 'fcra':
                return (
                    <>
                        <FcraDisclosureContent company={company} />
                        <label className="flex items-start gap-3 cursor-pointer mt-4">
                            <input
                                type="checkbox"
                                checked={requestEmailCopy}
                                onChange={(e) => setRequestEmailCopy(e.target.checked)}
                                className="mt-1 flex-shrink-0"
                            />
                            <span className="text-gray-700 text-sm">
                                I would like to receive a copy of my completed <strong>TruCheck</strong> identity and employability report via email.
                            </span>
                        </label>
                    </>
                )
            case 'universal_rights':
                return (
                    <div className="space-y-4 text-gray-700 text-sm">
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={requestEmailCopyDispute}
                                onChange={(e) => setRequestEmailCopyDispute(e.target.checked)}
                                className="mt-1 flex-shrink-0"
                            />
                            <span className="text-gray-700 text-sm">
                                I request to receive a copy of my completed <strong>TruCheck</strong> report via email.
                            </span>
                        </label>
                        <p>
                            <strong>Notice of Dispute:</strong> If your report is flagged for "Review Required," you have the right to dispute inaccurate information by contacting{' '}
                            <a
                                href="mailto:disputes.trucheck@verifiedid.co"
                                className="text-blue-600 underline"
                            >
                                disputes.trucheck@verifiedid.co
                            </a>
                            . VerifiedID LLC will investigate <strong>at no cost to you</strong>.
                        </p>
                    </div>
                )
            case 'summary':
                return <SummaryOfRightsContent />
            case 'dppa_glba':
                return <DppaGlbaContent />
            case 'esign':
                return (
                    <div className="space-y-3">
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={agreedFcra}
                                onChange={(e) => { setAgreedFcra(e.target.checked); setError('') }}
                                className="mt-1 flex-shrink-0"
                            />
                            <span className="text-gray-700 text-sm">
                                I <strong>ACKNOWLEDGE</strong> that I have received and read the FCRA Disclosure
                                and Summary of Rights. I <strong>AUTHORIZE</strong> TruCheck and its agents to
                                conduct identity validation as described.
                            </span>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={agreedDppaGlba}
                                onChange={(e) => { setAgreedDppaGlba(e.target.checked); setError('') }}
                                className="mt-1 flex-shrink-0"
                            />
                            <span className="text-gray-700 text-sm">
                                I, the undersigned, hereby provide my express written consent to{' '}
                                <strong>VerifiedID LLC and its authorized agents</strong> to access and verify my
                                personal information under the statutes of DPPA &amp; GLBA.
                            </span>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={agreedEsign}
                                onChange={(e) => { setAgreedEsign(e.target.checked); setError('') }}
                                className="mt-1 flex-shrink-0"
                            />
                            <span className="text-gray-700 text-sm">
                                I <strong>AGREE</strong> to conduct the identity verification process
                                electronically, as described in the ESIGN Act.
                            </span>
                        </label>
                    </div>
                )
            default:
                return null
        }
    }

    return (
        <div ref={refToPrint} className="flex items-center justify-center px-4 py-10">
            <div className="max-w-3xl w-full bg-white shadow-xl rounded-2xl p-8 space-y-6">

                <div className="space-y-2">
                    <h1 className="text-2xl font-bold text-gray-800">Legal Disclosures &amp; Consent</h1>
                    <p className="text-gray-700 text-base">
                        Please review and acknowledge the following disclosures to proceed.
                    </p>
                </div>

                <div className="space-y-2">
                    {SECTIONS.map((section) => {
                        const isOpen = expandedSections[section.id]
                        return (
                            <div key={section.id} className="border border-gray-200 rounded-xl overflow-hidden">
                                <button
                                    onClick={() => toggleSection(section.id)}
                                    className="w-full flex items-center justify-between px-5 py-4 text-left focus:outline-none hover:bg-gray-50 transition-colors"
                                >
                                    <span className={`font-semibold text-sm ${isOpen ? 'text-blue-700' : 'text-gray-800'}`}>
                                        {section.label}
                                    </span>
                                    <span className={`ml-4 flex-shrink-0 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                                        ▾
                                    </span>
                                </button>
                                {isOpen && (
                                    <div className="px-5 pb-5 border-t border-gray-100">
                                        <div className="pt-4">
                                            {getSectionContent(section.id)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>

                {error && (
                    <p className="text-sm text-red-600">{error}</p>
                )}

                <div className="text-center">
                    <button
                        onClick={handleNext}
                        disabled={!allChecked}
                        className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg shadow-md transition-all disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                    >
                        Acknowledge and Start Check
                    </button>
                </div>

            </div>
        </div>
    )

})

export default LegalDisclosuresAndConsentHub
